import React, { forwardRef, useEffect, useState } from 'react';
import { Input, GlobalLoading, InputNumber } from '@/shared/components';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { processNumberString } from '@/app/utils';
import { gridClassName } from './constants';
import { useGetProductByCatalogId } from '@/hooks/quick-order.hook';
import classnames from 'classnames';

type Value = Object;
interface IProps {
  deleteRows: (number) => void;
  addProductData: (value: Value, number, index) => void;
  keyVal: number;
  productData: any;
  clearAllClicked: boolean;
  updateQuantity: (string, number) => void;
  deleteRow: number[];
  updateClearAll: (boolean) => void;
  quantityVal?: number;
  catalogVal?: string;
}

const ManualEntryRow = forwardRef<HTMLInputElement, IProps>(function ManualEntryRow(props, ref) {
  const {
    deleteRows,
    addProductData,
    keyVal,
    productData,
    clearAllClicked,
    updateQuantity,
    deleteRow,
    updateClearAll,
    quantityVal,
  } = props;
  const { t } = useLang(NAMESPACE.quickOrder);

  const [catalogNumber, setCatalogNumber] = useState('');
  const [catalogNumberSearch, setCatalogNumberSearch] = useState('');
  const [quantity, setQuantity] = useState<number>(1);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const getProductByCatalogId = useGetProductByCatalogId();

  useEffect(() => {
    if (productData?.catalogNumber) {
      setCatalogNumber(productData.catalogNumber);
    }
  }, [productData]);

  const onQuantityChange = (value: string) => {
    setQuantity(processNumberString(value));
    if (Object.keys(productData).length > 0) {
      updateQuantity(keyVal, processNumberString(value));
    }
  };
  useEffect(() => {
    setQuantity(quantityVal);
  }, [quantityVal]);
  useEffect(() => {
    if (deleteRow.includes(keyVal)) {
      deleteData();
    }
  }, [deleteRow]);
  useEffect(() => {
    if (Object.keys(productData).length === 0 && !showError) {
      setCatalogNumber('');
      setQuantity(quantityVal ? quantityVal : 1);
    }
    if (productData?.error) {
      setShowError(true);
      setErrorMsg(productData?.error);
    }
  }, [productData]);
  useEffect(() => {
    if (clearAllClicked) {
      setCatalogNumber('');
      setQuantity(1);
      setShowError(false);
      updateClearAll(false);
    }
  }, [clearAllClicked]);
  const onSearch = () => {
    if (!catalogNumber?.trim()) return;
    GlobalLoading.show();
    setCatalogNumberSearch(catalogNumber);
    try {
      getProductByCatalogId.mutateAsync(catalogNumber?.trim()).then((response) => {
        GlobalLoading.hide();
        if (response.code === '200' && response.data?.length > 0) {
          addProductData(response.data[0], quantity, keyVal);
        } else {
          setShowError(true);
          setErrorMsg(response.message ? response.message : t('product.not.found'));
          addProductData(
            { catalogNumber: catalogNumber, error: response.message ? response.message : t('product.not.found') },
            quantity,
            keyVal
          );
        }
      });
    } catch (e) {
      GlobalLoading.hide();
    }
  };
  const handleSearchEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setShowError(false);
    setErrorMsg('');
    e.key === 'Enter' && onSearch();
  };
  const deleteData = () => {
    setCatalogNumber('');
    setQuantity(1);
    setShowError(false);
    deleteRows(keyVal);
  };
  const onBlur = () => {
    if (catalogNumber.length > 0 && catalogNumberSearch !== catalogNumber) {
      onSearch();
    }
  };
  return (
    <div className={`${gridClassName} mt-2 pb-2 sm:flex sm:flex-col`}>
      <div>
        <Input
          className="lg:w-[250px] lg:min-w-[200px] md:w-[220px] md:min-w-[220px] sm:w-[90%] sm:min-w-[150px]"
          placeholder={t('input.catalog.value')}
          label={t('catalog.number')}
          labelClassName="hidden sm:block"
          value={catalogNumber ? catalogNumber : productData?.catalogNumber}
          onChange={(e) => {
            setCatalogNumber(e.target.value);
          }}
          onKeyDown={handleSearchEnterKey}
          onBlur={onBlur}
        />
        {showError && <p className="text-[red]">{errorMsg}</p>}
      </div>

      <div className="flex gap-x-2">
        <InputNumber
          className="w-[104px] flex-shrink-0 sm:mt-2"
          textAlign="center"
          minValue="1"
          maxValue="9999"
          value={quantity}
          onChange={onQuantityChange}
        />
      </div>

      {productData.productTitle || productData.productTitle === null ? (
        <div className="text-[grey] overflow-hidden whitespace-nowrap overflow-ellipsis my-auto sm:w-[90%] sm:my-0">
          <span className="hidden sm:block text-[black] text-sm font-medium">{t('product.name')}</span>
          {productData.productTitle === null ? 'NA' : productData.productTitle}
        </div>
      ) : (
        <div className="text-[grey] my-auto sm:hidden">{t('product.name.placeholder')}</div>
      )}
      {productData?.unitSize ? (
        <div className="sm:w-[90%] my-auto">
          <span className="hidden sm:block text-[black] text-sm font-medium">{t('size')}</span>
          <div className="text-[grey] my-auto sm:my-0">{productData.unitSize}</div>
        </div>
      ) : (
        <div className="text-[grey] my-auto sm:hidden">{t('unit.size.placeholder')}</div>
      )}
      <div
        className={classnames('my-auto ml-auto sm:my-0 sm:ml-auto sm:mr-[50px]', {
          'text-[grey]': !productData?.catalogNumber,
          'text-[#1e8ae7]': productData?.catalogNumber,
        })}
        onClick={deleteData}
      >
        <i className={classnames('icon-24-bin-mono sm:hidden', { 'cursor-pointer': productData?.catalogNumber })} />
        <i className="icon-24-bin-mono hidden sm:block" />
      </div>
    </div>
  );
});
export { ManualEntryRow };
